<script>
    import { onMount, onDestroy } from 'svelte';
    import { tweened } from 'svelte/motion';
    import { linear } from 'svelte/easing';
    import { toast } from './stores.js';
    import CheckCircleFilledIcon from '$lib/components/foundation/icons/CheckCircleFilledIcon.component.svelte';
    import CheckIcon from '$lib/components/foundation/icons/CheckIcon.component.svelte';
    import CancelIcon from '$lib/components/foundation/icons/CancelIcon.component.svelte';
    import AlertTriangleIcon from '$lib/components/foundation/icons/AlertTriangleIcon.component.svelte';
    import InfoIcon from '$lib/components/foundation/icons/InfoIcon.component.svelte';

    /** @type {import('./stores.js').SvelteToastOptions} */
    export let item;

    /** @type {any} */
    let next = item.initial;
    let prev = next;
    let paused = false;
    let cprops = {};
    /** @type {any} */
    let unlisten;

    const progress = tweened(item.initial, { duration: item.duration, easing: linear });

    function close() {
        toast.pop(item.id);
    }

    function autoclose() {
        if ($progress === 1 || $progress === 0) close();
    }

    function pause() {
        if (!paused && $progress !== next) {
            progress.set($progress, { duration: 0 });
            paused = true;
        }
    }

    function resume() {
        if (paused) {
            const d = /** @type {any} */ (item.duration);
            const duration = d - d * (($progress - prev) / (next - prev));
            progress.set(next, { duration }).then(autoclose);
            paused = false;
        }
    }

    /** @param {any} prop */
    function check(prop, kind = 'undefined') {
        return typeof prop === kind;
    }

    function listen(d = document) {
        if (check(d.hidden)) return;
        const handler = () => (d.hidden ? pause() : resume());
        const name = 'visibilitychange';
        d.addEventListener(name, handler);
        unlisten = () => d.removeEventListener(name, handler);
        handler();
    }

    $: if (next !== item.next) {
        next = item.next;
        prev = $progress;
        paused = false;
        progress.set(next).then(autoclose);
    }

    $: if (item.component) {
        const { props = {}, sendIdTo } = item.component;
        cprops = { ...props, ...(sendIdTo && { [sendIdTo]: item.id }) };
    }

    // `progress` has been renamed to `next`; shim included for backward compatibility, to remove in next major
    $: if (!check(item.progress)) {
        item.next = item.progress;
    }

    onMount(listen);

    onDestroy(() => {
        if (check(item.onpop, 'function')) {
            // @ts-ignore
            item.onpop(item.id);
        }
        unlisten && unlisten();
    });
</script>

<div
    role="status"
    class="_toastItem"
    class:pe={item.pausable}
    on:mouseenter={() => {
        if (item.pausable) pause();
    }}
    on:mouseleave={resume}>
    <div class="_toastMsg" class:pe={item.component}>
        {#if item.component}
            <svelte:component this={item.component.src} {...cprops} />
        {:else}
            {#if item.theme}
                {#if item.theme === 'success'}
                    <CheckIcon class="mr-2 float-left" />
                {:else if item.theme === 'danger'}
                    <CancelIcon class="mr-2 float-left" />
                {:else if item.theme === 'warning'}
                    <AlertTriangleIcon class="mr-2 float-left" />
                {:else}
                    <InfoIcon class="mr-2 float-left" />
                {/if}
            {/if}

            {@html item.msg}
        {/if}
    </div>
    {#if item.dismissable}
        <div
            class="_toastBtn pe"
            role="button"
            tabindex="0"
            on:click={close}
            on:keydown={(e) => {
                if (e instanceof KeyboardEvent && ['Enter', ' '].includes(e.key)) close();
            }} />
    {/if}
    <progress class="_toastBar" value={$progress} />
</div>
